<template>
  <legal-layout>
    <router-link
      class="legal-back"
      :to="{ name: 'legal' }"
    >
      <feather-icon
        icon="ArrowLeftIcon"
        size="16"
      />
      {{ $t('legal.center') }}
    </router-link>

    <div class="legal-doc">
      <article class="legal-card legal-doc__article">
        <h1 class="legal-title">
          {{ title }}
        </h1>

        <div
          v-if="state === 'loading'"
          class="legal-doc__status"
        >
          <b-spinner
            small
            class="mr-50"
          />
          {{ $t('legal.loading') }}
        </div>

        <div
          v-else-if="state === 'error'"
          class="legal-doc__status"
        >
          {{ $t('legal.loadError') }}
          <button
            type="button"
            class="legal-doc__retry"
            @click="load"
          >
            {{ $t('legal.retry') }}
          </button>
        </div>

        <template v-else>
          <nav
            v-if="sections.length > 2"
            class="legal-doc__toc"
            :aria-label="$t('legal.onThisPage')"
            :lang="bodyLang"
            :dir="bodyDir"
          >
            <p class="legal-doc__toc-title">
              {{ $t('legal.onThisPage') }}
            </p>
            <ol>
              <li
                v-for="section in sections"
                :key="section.id"
              >
                <a
                  :href="`#${section.id}`"
                  @click.prevent="scrollTo(section.id)"
                >{{ section.heading }}</a>
              </li>
            </ol>
          </nav>

          <div
            class="legal-doc__body"
            :lang="bodyLang"
            :dir="bodyDir"
            @click="onBodyClick"
          >
            <section
              v-for="(section, index) in sections"
              :id="section.id"
              :key="section.id"
              class="legal-doc__section"
            >
              <h2>{{ index + 1 }}. {{ section.heading }}</h2>
              <p v-html="section.html" />
            </section>
          </div>

          <p class="legal-note">
            {{ $t('legal.operatedBy', { brand: info.brand, company: info.company }).replace('..', '.') }}
            <template v-if="$i18n.locale !== bodyLang">
              {{ $t('legal.translationNote') }}
            </template>
          </p>
        </template>
      </article>

      <aside class="legal-doc__aside">
        <p class="legal-doc__aside-title">
          {{ $t('legal.related') }}
        </p>
        <ul>
          <li
            v-for="item in related"
            :key="item.slug"
          >
            <router-link :to="{ name: 'legal.document', params: { slug: item.slug } }">
              {{ $t(`legal.docs.${item.key}`) }}
            </router-link>
          </li>
          <li>
            <router-link :to="{ name: 'legal.report' }">
              {{ $t('legal.reportContent') }}
            </router-link>
          </li>
        </ul>
      </aside>
    </div>
  </legal-layout>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import {
  LEGAL_BODY_LOCALE, LEGAL_DOCS, LEGAL_INFO, findLegalDoc, loadLegalContent, renderLegalText,
} from '@/libs/legal'
import LegalLayout from './LegalLayout.vue'

export default {
  name: 'LegalDocument',
  components: { LegalLayout, BSpinner },
  data() {
    return {
      info: LEGAL_INFO,
      state: 'loading',
      raw: [],
    }
  },
  computed: {
    slug() {
      return this.$route.params.slug
    },
    doc() {
      return findLegalDoc(this.slug)
    },
    title() {
      return this.doc ? this.$t(`legal.docs.${this.doc.key}`) : ''
    },
    sections() {
      return this.raw.map(([heading, text], i) => ({
        id: `s${i + 1}`,
        heading,
        html: renderLegalText(text, this.info),
      }))
    },
    // Metin gövdesinin dili (varsayılan: standart İngilizce)
    bodyLang() {
      return LEGAL_BODY_LOCALE || this.$i18n.locale
    },
    bodyDir() {
      return this.bodyLang === 'ar' ? 'rtl' : 'ltr'
    },
    related() {
      return LEGAL_DOCS.filter(d => d.slug !== this.slug)
    },
  },
  watch: {
    slug: 'load',
    '$i18n.locale': 'load',
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      if (!this.doc) {
        this.$router.replace({ name: 'legal' })
        return
      }
      const { slug } = this
      const { locale } = this.$i18n
      this.state = 'loading'
      document.title = `${this.title} · ${this.info.brand}`
      loadLegalContent(locale)
        .then(content => {
          if (slug !== this.slug || locale !== this.$i18n.locale) return
          this.raw = content[slug] || []
          this.state = 'ready'
          if (this.$route.hash) this.$nextTick(() => this.scrollTo(this.$route.hash.slice(1)))
        })
        .catch(() => {
          if (slug === this.slug) this.state = 'error'
        })
    },
    scrollTo(id) {
      const el = document.getElementById(id)
      if (!el) return
      const top = el.getBoundingClientRect().top + window.pageYOffset - 80
      window.scrollTo({ top, behavior: 'smooth' })
    },
    // Metin içindeki diğer sözleşme bağlantıları sayfayı yeniden yüklemeden açılsın
    onBodyClick(event) {
      const link = event.target.closest && event.target.closest('a[data-legal-link]')
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
      event.preventDefault()
      this.$router.push({ name: 'legal.document', params: { slug: link.getAttribute('data-legal-link') } })
    },
  },
}
</script>

<style lang="scss" scoped>
.legal-doc {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 24px;
  align-items: start;
}

.legal-doc__status {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 0;
  color: rgba(236, 232, 255, 0.75);
}

.legal-doc__retry {
  border: 1px solid rgba(150, 110, 255, 0.4);
  background: transparent;
  color: #fff;
  border-radius: 999px;
  padding: 4px 14px;
}

.legal-doc__toc {
  margin: 18px 0 8px;
  padding: 16px 20px;
  border: 1px solid rgba(150, 110, 255, 0.2);
  border-radius: 14px;
  background: rgba(15, 8, 38, 0.4);

  ol {
    margin: 0;
    padding-inline-start: 20px;
    columns: 2;
    column-gap: 28px;
  }

  li {
    break-inside: avoid;
    margin-bottom: 6px;
    color: rgba(236, 232, 255, 0.55);
  }

  a {
    color: rgba(236, 232, 255, 0.85);

    &:hover {
      color: #f472b6;
    }
  }
}

.legal-doc__toc-title,
.legal-doc__aside-title {
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  margin: 0 0 10px;
}

.legal-doc__section {
  padding-top: 22px;

  h2 {
    color: #fff !important;
    font-size: 19px;
    font-weight: 700;
    margin: 0 0 10px;
  }

  p {
    margin: 0;
    font-size: 15.5px;
    line-height: 1.75;
    color: rgba(236, 232, 255, 0.86);
  }

  ::v-deep a {
    color: #f472b6;
    text-decoration: underline;
    text-underline-offset: 2px;
    word-break: break-word;
  }
}

.legal-doc__aside {
  position: sticky;
  top: 76px;
  padding: 20px;
  border: 1px solid rgba(150, 110, 255, 0.22);
  border-radius: 16px;
  background: rgba(32, 22, 72, 0.55);

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li + li {
    margin-top: 10px;
  }

  a {
    color: rgba(236, 232, 255, 0.85);
    font-size: 14px;

    &:hover {
      color: #f472b6;
    }
  }
}

@media (max-width: 991px) {
  .legal-doc {
    grid-template-columns: 1fr;
  }

  .legal-doc__aside {
    position: static;
  }
}

@media (max-width: 575px) {
  .legal-doc__toc ol {
    columns: 1;
  }

  .legal-doc__section h2 {
    font-size: 17px;
  }
}
</style>
